import { createReducer, on } from '@ngrx/store';
import { ConversationalState } from '../interfaces/conversational-state.interface';
import { ConversationalActionTypes, ConversationalActions } from './conversational.actions';
import { AUTHOR_TYPES, CONVERSATIONAL_MESSAGE_TYPES, EXCHANGE_ACTIONS } from './conversational.constants';
import { ContextExchange, ContextItem, ExchangeItem } from './conversational.models';

export const initialConversationalState: ConversationalState = {
  loading: false,
  error: null,
  activeContextId: null,
  external: {},
  listeners: [],
  contexts: {}
};

const reducer = createReducer(
  initialConversationalState,

  // Load all contexts
  on(
    ConversationalActions.loadHistoryContexts,
    (state): ConversationalState => ({
      ...state,
      loading: true,
      error: null
    })
  ),
  on(
    ConversationalActions.loadHistoryContextsSuccess,
    (state, { contexts }): ConversationalState => ({
      ...state,
      loading: false,
      contexts
    })
  ),
  on(
    ConversationalActions.loadHistoryContextsFailure,
    (state, { error }): ConversationalState => ({
      ...state,
      loading: false,
      error
    })
  ),

  // Load a context exchanges
  on(
    ConversationalActions.loadContextExchanges,
    (state, { contextId }): ConversationalState => ({
      ...state,
      contexts: {
        ...state.contexts,
        [contextId]: {
          ...state.contexts[contextId],
          loading: true,
          error: null
        }
      }
    })
  ),
  on(
    ConversationalActions.loadContextExchangesSuccess,
    (state, { exchanges, contextId }): ConversationalState => ({
      ...state,
      contexts: {
        ...state.contexts,
        [contextId]: {
          ...state.contexts[contextId],
          loading: false,
          exchanges: exchanges.map((exchange, index) => {
            return { ...exchange, contextId, hideActions: index !== exchanges.length - 1 };
          }),
          error: null
        }
      }
    })
  ),
  on(
    ConversationalActions.loadContextExchangesFailure,
    (state, { contextId, error }): ConversationalState => ({
      ...state,
      contexts: {
        ...state.contexts,
        [contextId]: {
          ...state.contexts[contextId],
          loading: false,
          error
        }
      }
    })
  ),

  // Delete a context
  on(ConversationalActions.deleteContexts, (state, { contextIds }): ConversationalState => {
    const newContexts: { [contextId: string]: ContextItem } = {};

    contextIds.forEach((id) => {
      newContexts[id] = { ...state.contexts[id], loading: true, error: null };
    });

    return {
      ...state,
      contexts: { ...state.contexts, ...newContexts }
    };
  }),
  on(ConversationalActions.deleteContextsSuccess, (state, { contextIds }): ConversationalState => {
    const remainingContexts: { [contextId: string]: ContextItem } = {};

    Object.keys(state.contexts).forEach((contextId) => {
      if (!contextIds.includes(contextId)) {
        remainingContexts[contextId] = { ...state.contexts[contextId] };
      }
    });

    return {
      ...state,
      contexts: { ...remainingContexts }
    };
  }),
  on(ConversationalActions.deleteContextsFailure, (state, { contextIds, error }): ConversationalState => {
    const newContexts: { [contextId: string]: ContextItem } = {};

    contextIds.forEach((id) => {
      newContexts[id] = { ...state.contexts[id], loading: false, error };
    });

    return {
      ...state,
      contexts: { ...state.contexts, ...newContexts }
    };
  }),

  // Add a new exchange
  on(
    ConversationalActions.addExchange,
    (state, { exchange }): ConversationalState => ({
      ...state,
      contexts: {
        ...state.contexts,
        [exchange.contextId]: {
          ...state.contexts[exchange.contextId],
          contextId: state.contexts[exchange.contextId]?.contextId ?? exchange.contextId,
          exchanges: [...(state.contexts[exchange.contextId]?.exchanges || []), exchange],
          title: state.contexts[exchange.contextId]?.title ?? exchange.text,
          date: state.contexts[exchange.contextId]?.date ?? Date.now()
        }
      }
    })
  ),

  // Update an exchange
  on(ConversationalActions.updateExchange, (state, { contextId, exchangeId, exchange }): ConversationalState => {
    return {
      ...state,
      contexts: {
        ...state.contexts,
        [contextId]: {
          ...state.contexts[contextId],
          exchanges: (state.contexts[contextId].exchanges as Array<ExchangeItem>).map((_exchange) =>
            exchangeId === _exchange['id'] ? { ..._exchange, ...exchange } : _exchange
          )
        }
      }
    };
  }),

  // Set a selected context
  on(
    ConversationalActions.setActiveContext,
    (state, { contextId }): ConversationalState => ({
      ...state,
      activeContextId: contextId
    })
  ),

  // Hide context actions
  on(ConversationalActions.hideContextActions, (state, { contextId }) => ({
    ...state,
    contexts: {
      ...state.contexts,
      [contextId]: {
        ...state.contexts[contextId],
        exchanges: state.contexts[contextId].exchanges.map((_exchange) => ({ ..._exchange, hideActions: true }))
      }
    }
  })),

  // Update Context ID
  on(ConversationalActions.updateContextID, (state, { contextId, newContextId }) => {
    if (contextId === newContextId) {
      return state; // no change needed
    }

    const updatedContexts: { [contextId: string]: ContextItem } = {};

    Object.keys(state.contexts).forEach((id) => {
      if (id === contextId) {
        updatedContexts[newContextId] = {
          ...state.contexts[id],
          contextId: newContextId,
          exchanges: [...state.contexts[id].exchanges.map((_exchange) => ({ ..._exchange, contextId: newContextId }))]
        };
      } else {
        updatedContexts[id] = state.contexts[id];
      }
    });

    return {
      ...state,
      contexts: updatedContexts
    };
  }),

  // LEGACY - START (This will be replaced with the send exchange as for radar in future)
  on(ConversationalActions.sendPrompt, (state, { userExchange }): ConversationalState => {
    // The sendPrompt and sendCreateElement actions will become obsolete soon and all messages will be handled through the same endpoint,
    // they have all these weird string id's because currently the backend doesn't assign separate id's for separate exchanges yet, this is being worked on for the radar creation.
    // Radar will use the new way of working and as soon as we have
    const contextId = userExchange.contextId ?? 'temp_' + Date.now().toString();

    const responseExchange: ExchangeItem = {
      id: 'response_' + userExchange.id,
      author: AUTHOR_TYPES.GPT,
      text: '',
      contextId,
      loading: true
    };

    return {
      ...state,
      activeContextId: contextId,
      contexts: {
        ...state.contexts,
        [contextId]: {
          ...state.contexts[contextId],
          contextId: state.contexts[contextId]?.contextId ?? contextId,
          exchanges: [
            ...(state.contexts[contextId]?.exchanges.map((ex) => ({ ...ex, hideActions: true })) || []),
            { ...userExchange, contextId: contextId },
            responseExchange
          ],
          title: state.contexts[contextId]?.title ?? userExchange.text,
          date: Date.now()
        }
      }
    };
  }),
  on(ConversationalActions.sendPromptSuccess, (state, { userExchange, response }): ConversationalState => {
    let exchanges = state.contexts[response.contextId]?.exchanges ?? [];
    if (userExchange.contextId !== response.contextId) {
      exchanges = state.contexts[userExchange.contextId].exchanges ?? [];
    }

    exchanges = exchanges.map((_exchange) => {
      if (_exchange['id'] === 'response_' + userExchange.id) {
        return { ..._exchange, requestId: response.requestId, contextId: response.contextId };
      }
      return { ..._exchange, contextId: response.contextId, id: Date.now().toString() };
    });

    return {
      ...state,
      activeContextId: response.contextId,
      listeners: [...state.listeners, { contextId: response.contextId, requestId: response.requestId }],
      contexts: {
        ...state.contexts,
        [response.contextId]: {
          ...state.contexts[userExchange.contextId],
          ...state.contexts[response.contextId],
          contextId: response.contextId,
          exchanges
        }
      }
    };
  }),
  on(ConversationalActions.sendCreateElement, (state, { newExchange }): ConversationalState => {
    return {
      ...state,
      activeContextId: newExchange.contextId,
      contexts: {
        ...state.contexts,
        [newExchange.contextId]: {
          ...state.contexts[newExchange.contextId],
          exchanges: [...(state.contexts[newExchange.contextId]?.exchanges.map((ex) => ({ ...ex, hideActions: true })) || []), newExchange],
          date: Date.now()
        }
      }
    };
  }),
  on(ConversationalActions.sendCreateElementSuccess, (state, { newExchange, response }): ConversationalState => {
    return {
      ...state,
      activeContextId: response.contextId,
      listeners: [...state.listeners, { contextId: response.contextId, requestId: response.requestId }],
      contexts: {
        ...state.contexts,
        [response.contextId]: {
          ...state.contexts[response.contextId],
          exchanges: state.contexts[response.contextId].exchanges.map((_exchange) => {
            if (_exchange['id'] === newExchange.id) {
              return { ..._exchange, requestId: response.requestId };
            }
            return _exchange;
          })
        }
      }
    };
  }),
  // LEGACY - END

  // Exchanges (Radar)
  on(ConversationalActions.sendExchange, (state, { contextId, outputExchangeId, exchange }): ConversationalState => {
    // Creating an empty exchange to show the loading state
    const exchanges = [...(state.contexts[contextId]?.exchanges ?? [])];
    const existingExchange = exchanges.find((_exchange: ContextExchange) => _exchange.exchangeId === exchange.exchangeId);
    const existingOutputExchange = exchanges.find((_exchange: ContextExchange) => _exchange.exchangeId === outputExchangeId);

    if (!existingExchange) {
      exchanges.push(exchange);
    } else {
      exchanges[exchanges.indexOf(existingExchange)] = { ...existingExchange, loading: true, hideActions: exchange.hideActions || false };
    }

    if (!existingOutputExchange) {
      exchanges.push({
        contextId,
        exchangeId: outputExchangeId,
        loading: true,
        author: AUTHOR_TYPES.GPT
      });
    } else {
      exchanges[exchanges.indexOf(existingOutputExchange)] = { ...existingOutputExchange, loading: true };
    }

    return {
      ...state,
      activeContextId: contextId,
      contexts: {
        ...state.contexts,
        [contextId]: {
          ...state.contexts[contextId],
          contextId: contextId,
          title:
            state.contexts[contextId]?.title ??
            (exchange.actions.some((action) => action.action === EXCHANGE_ACTIONS.RADAR_START)
              ? (exchange.data?.elementType?.name ?? 'Create') + ' Radar'
              : exchange.message),
          date: Date.now(),
          exchanges
        }
      }
    };
  }),
  on(ConversationalActions.sendExchangeSuccess, (state, { contextId, exchange, response }): ConversationalState => {
    return {
      ...state,
      activeContextId: contextId,
      contexts: {
        ...state.contexts,
        [contextId]: {
          ...state.contexts[contextId],
          contextId: response.contextId,
          title: state.contexts[contextId]?.title ?? exchange.message,
          exchanges: state.contexts[contextId]?.exchanges ?? [],
          date: state.contexts[contextId]?.date ?? Date.now(),
          loading: false
        }
      }
    };
  }),
  on(ConversationalActions.sendExchangeFailure, (state, { contextId, outputExchangeId, exchange, error }): ConversationalState => {
    return {
      ...state,
      activeContextId: contextId,
      contexts: {
        ...state.contexts,
        [contextId]: {
          ...state.contexts[contextId],
          contextId,
          title: state.contexts[contextId]?.title ?? exchange.message,
          exchanges: (state.contexts[contextId]?.exchanges ?? []).map((_exchange: ContextExchange) => {
            if (_exchange.exchangeId === outputExchangeId) {
              return { ..._exchange, error, type: CONVERSATIONAL_MESSAGE_TYPES.ERROR };
            }
            return _exchange;
          }),
          date: state.contexts[contextId]?.date ?? Date.now(),
          loading: false
        }
      }
    };
  }),
  on(ConversationalActions.updateContextExchange, (state, { contextId, exchange }): ConversationalState => {
    let exchanges = [...((state.contexts[contextId]?.exchanges ?? []) as Array<ContextExchange>)];
    if (!exchanges.some((_exchange) => _exchange.exchangeId === exchange.exchangeId)) {
      exchanges.push(exchange);
    }
    exchanges = exchanges.map((_exchange) => ({
      ...(exchange.exchangeId === _exchange.exchangeId
        ? { ..._exchange, ...exchange }
        : { ..._exchange, contextId: exchange.contextId ?? _exchange.contextId, hideActions: true }),
      contextId
    }));

    return {
      ...state,
      contexts: {
        ...state.contexts,
        [contextId]: {
          ...state.contexts[contextId],
          contextId: exchange.contextId,
          exchanges
        }
      }
    };
  }),
  on(
    ConversationalActions.setRadarCommand,
    (state, { action, value }): ConversationalState => ({
      ...state,
      external: {
        ...state.external,
        radarCommand: { action, value }
      }
    })
  )
);

export const ConversationalReducer = (state = initialConversationalState, actions: ConversationalActionTypes): ConversationalState => {
  return reducer(state, actions);
};
